import React from "react";
import { faCaretRight } from "@fortawesome/pro-solid-svg-icons";
import { getAllActivePublications } from "@plugins/gatsby-source-paligo/src/config";
import { PublicationsConfigEntry } from "@plugins/gatsby-source-paligo/src/types";
import { Paragraph } from "@singlestore/fusion/components/typography";
import { useSiteContext } from "contexts/SiteContext";
import { navigate } from "gatsby";
import TreeView from "react-accessible-treeview";

import { FaIcon } from "components/FaIcon/FaIcon";
import { Link } from "components/Link/Link";

import { ProductVersionNodeForNavType } from "types/types";

import "./NavigationTree.scss";

const publications = getAllActivePublications();

// this is a mixed type which must accomodate for product version nodes and
// document nodes
export type RootNavigationTreeNode = {
    name: string;
    shortName?: string;
    path?: string;
    position?: number;
    paligoId?: number;
    productNode?: { paligoId: number };
    children?: Array<RootNavigationTreeNode>;
};

export type NavigationTreeNode = {
    id: number;
    name: string;
    shortName?: string;
    children: Array<number>;
    parent: number | null;
    path?: string;
    paligoId?: number;
    position: number;
    isBranch?: boolean;
};

// we must convert our data to a flat array to work with react-accessible-treeview
// see here: https://github.com/dgreene1/react-accessible-treeview#data
export const flattenTree = (
    productVersionNode: ProductVersionNodeForNavType
): Array<NavigationTreeNode> => {
    const flattenedTree: Array<NavigationTreeNode> = [];

    const publicationConfig = publications.find(
        ({ id }) => id === productVersionNode.productNode?.paligoId
    ) as PublicationsConfigEntry;

    const flattenTreeHelper = (
        tree: RootNavigationTreeNode,
        parent: number | null
    ): void => {
        const node: NavigationTreeNode = {
            id: flattenedTree.length,
            name: tree.name,
            shortName: tree.shortName,
            children: [],
            parent,
            path: tree.path,
            paligoId: tree.paligoId,
            position: tree?.position || 0,
        };

        flattenedTree.push(node);

        if (
            !tree.children?.length ||
            (tree.paligoId &&
                publicationConfig.hideChildrenInNavbar?.includes(tree.paligoId))
        ) {
            return;
        }

        for (const child of tree.children) {
            flattenTreeHelper(child, node.id);
        }

        node.children = flattenedTree
            .filter((x) => x.parent === node.id)
            .sort((a, b) => a.position - b.position)
            .map((x: NavigationTreeNode) => x.id);
    };

    flattenTreeHelper(productVersionNode, null);

    return flattenedTree;
};

type NavigationTreeProps = {
    productVersionNode: ProductVersionNodeForNavType;
    beforeNavigate?: (path: string, elementName: string) => void;
};

export function NavigationTree({
    productVersionNode,
    beforeNavigate,
}: NavigationTreeProps) {
    const { pathname } = useSiteContext();

    const sidebarData = React.useMemo(
        () => flattenTree(productVersionNode),
        [productVersionNode]
    );

    const publicationConfig = publications.find(
        ({ id }) => id === productVersionNode.productNode?.paligoId
    ) as PublicationsConfigEntry;

    const isCurrentPathOrParentOf = React.useCallback(
        (node?: NavigationTreeNode): boolean => {
            if (node) {
                if (node.path === pathname) {
                    return true;
                }

                // if this node is hiding its children, and pathname is an
                // immediate child then we should highlight
                if (
                    node.paligoId &&
                    publicationConfig.hideChildrenInNavbar?.includes(
                        node.paligoId
                    )
                ) {
                    const pathnameParts = pathname.split("/").filter(Boolean);
                    pathnameParts.pop();
                    const parentPathname = `/${pathnameParts.join("/")}/`;

                    return node.path === parentPathname;
                }

                if (node.children) {
                    return node.children.some((child: number) => {
                        return isCurrentPathOrParentOf(sidebarData?.[child]);
                    });
                }
            }

            return false;
        },
        [pathname, sidebarData, publicationConfig.hideChildrenInNavbar]
    );

    const defaultExpandedIds = React.useMemo(() => {
        const expandedIds: Array<number> = [];

        sidebarData?.forEach((node) => {
            if (isCurrentPathOrParentOf(node)) {
                expandedIds.push(node.id);
            }
        });

        return expandedIds;
    }, [sidebarData, isCurrentPathOrParentOf]);

    return (
        <TreeView
            data={sidebarData}
            aria-label="Document navigation"
            defaultExpandedIds={defaultExpandedIds}
            nodeRenderer={({
                element: _element,
                isBranch,
                isExpanded,
                level,
                getNodeProps,
                handleExpand,
            }) => {
                const element = _element as NavigationTreeNode;

                const nodeProps = getNodeProps({
                    onClick: (e) => {
                        beforeNavigate?.(
                            element.path as string,
                            element.shortName || ""
                        );

                        if (!isExpanded) {
                            handleExpand(e);
                        }
                    },
                });

                let caret;
                if (isBranch) {
                    caret = (
                        <FaIcon
                            className="tree-node-expanded-indicator"
                            icon={faCaretRight}
                            onClick={(e) => {
                                e.preventDefault();
                                if (isExpanded) {
                                    handleExpand(e);
                                }
                            }}
                        />
                    );
                }

                const boldText =
                    level === 1 ||
                    isExpanded ||
                    element.path === pathname ||
                    defaultExpandedIds.includes(element.id);

                return (
                    <Paragraph
                        variant={boldText ? "heading-2" : "body-3"}
                        color="mid-contrast"
                        lineHeight="2"
                    >
                        <Link
                            to={element.path as string}
                            aria-current={element.path === pathname}
                            tabIndex={-1}
                            {...nodeProps}
                            ref={
                                "ref" in nodeProps
                                    ? (nodeProps.ref as React.Ref<any>)
                                    : undefined
                            }
                            styled={false}
                        >
                            {caret}
                            {element.shortName || ""}
                        </Link>
                    </Paragraph>
                );
            }}
            onSelect={({ element }) => {
                const node = element as NavigationTreeNode;

                if (node.path) {
                    beforeNavigate?.(node.path, node.shortName || "");
                    navigate(node.path);
                }
            }}
        />
    );
}
